import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import apiService from '../services/apiService';
import './SetupQueuePage.css';
import { 
  getAvailableEnvironments, 
  getSelectedEnvironment, 
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';

const SetupQueuePage = () => {
  const [mode, setMode] = useState('single'); // 'single' or 'bulk'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(true);

  // Environment selection
  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());
  const availableEnvironments = getAvailableEnvironments();

  // Get user role from localStorage
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;
  const userRole = user?.role || '';
  const isStandardUser = userRole.toUpperCase() === 'STANDARD';

  // Get API endpoints based on selected environment
  const apiEndpoints = getApiEndpoints(selectedEnvironment);
  const API_ENDPOINT = apiEndpoints.queueApi;
  const INSTANCE_DROPDOWN_API = apiEndpoints.instanceDropdown;

  // Single queue form state
  const [singleForm, setSingleForm] = useState({
    instance_id: '',
    name: '',
    description: '',
    hours_of_operation_id: '',
    max_contacts: '',
    outbound_caller_id_name: '',
    outbound_caller_id_number_id: '',
    outbound_flow_id: '',
    quick_connect_ids: ''
  });

  // Bulk form state
  const [bulkForm, setBulkForm] = useState({
    instance_id: '',
    s3_bucket: '',
    s3_key: ''
  });

  // Handle environment change
  const handleEnvironmentChange = (e) => {
    const newEnvironment = e.target.value;
    setSelectedEnvironment(newEnvironment);
    saveSelectedEnvironment(newEnvironment);
    
    // Clear all dropdowns and form data when environment changes
    setInstances([]);
    setSingleForm({
      instance_id: '',
      name: '',
      description: '',
      hours_of_operation_id: '',
      max_contacts: '',
      outbound_caller_id_name: '',
      outbound_caller_id_number_id: '',
      outbound_flow_id: '',
      quick_connect_ids: ''
    });
    setBulkForm({
      instance_id: '',
      s3_bucket: '',
      s3_key: ''
    });
    setResult(null);
    setError(null);
  };

  const handleSingleFormChange = (e) => {
    const { name, value } = e.target;
    setSingleForm(prev => ({ ...prev, [name]: value }));
  };

  const handleBulkFormChange = (e) => {
    const { name, value } = e.target;
    setBulkForm(prev => ({ ...prev, [name]: value }));
  };

  // Fetch Connect instances on component mount and when environment changes
  useEffect(() => {
    const fetchInstances = async () => {
      try {
        setLoadingInstances(true);
        const response = await apiService.get(INSTANCE_DROPDOWN_API);
        if (response.data && response.data.instances) {
          setInstances(response.data.instances);
        }
      } catch (err) {
        console.error('Error fetching instances:', err);
        setError('Failed to load Connect instances. Please refresh the page.');
      } finally {
        setLoadingInstances(false);
      }
    };

    fetchInstances();
  }, [INSTANCE_DROPDOWN_API, selectedEnvironment]);

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        mode: 'single',
        instance_id: singleForm.instance_id,
        queue: {
          Name: singleForm.name,
          HoursOfOperationId: singleForm.hours_of_operation_id
        }
      };

      // Add optional fields only if provided
      if (singleForm.description) {
        payload.queue.Description = singleForm.description;
      }

      if (singleForm.max_contacts) {
        payload.queue.MaxContacts = parseInt(singleForm.max_contacts, 10);
      }

      // Add OutboundCallerConfig as a nested object (Lambda supports both nested and flat)
      if (singleForm.outbound_caller_id_name || singleForm.outbound_caller_id_number_id || singleForm.outbound_flow_id) {
        payload.queue.OutboundCallerConfig = {};
        
        if (singleForm.outbound_caller_id_name) {
          payload.queue.OutboundCallerConfig.OutboundCallerIdName = singleForm.outbound_caller_id_name;
        }
        if (singleForm.outbound_caller_id_number_id) {
          payload.queue.OutboundCallerConfig.OutboundCallerIdNumberId = singleForm.outbound_caller_id_number_id;
        }
        if (singleForm.outbound_flow_id) {
          payload.queue.OutboundCallerConfig.OutboundFlowId = singleForm.outbound_flow_id;
        }
      }

      if (singleForm.quick_connect_ids) {
        payload.queue.QuickConnectIds = singleForm.quick_connect_ids
          .split(',')
          .map(id => id.trim())
          .filter(id => id);
      }

      const response = await apiClient.post(API_ENDPOINT, payload);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create queue');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        mode: 'bulk',
        instance_id: bulkForm.instance_id,
        s3_bucket: bulkForm.s3_bucket,
        s3_key: bulkForm.s3_key
      };

      const response = await apiClient.post(API_ENDPOINT, payload);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to process bulk queues');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="setup-page">
      <div className="page-header">
        <div>
          <h1>Setup Queue</h1>
          <p className="page-subtitle">Create Amazon Connect queues individually or in bulk</p>
        </div>
        <Link to="/home" className="btn btn-secondary">
          ← Back to Home
        </Link>
      </div>

      {/* Environment Selector */}
      <div className="environment-selector" style={{
        backgroundColor: '#f9f9f9',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <label htmlFor="environment" style={{ 
          fontWeight: 'bold', 
          fontSize: '16px',
          minWidth: '120px'
        }}>
          Environment:
        </label>
        <select
          id="environment"
          value={selectedEnvironment}
          onChange={handleEnvironmentChange}
          style={{
            padding: '10px 16px',
            fontSize: '15px',
            borderRadius: '6px',
            border: '2px solid #ccc',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontWeight: '500',
            minWidth: '200px'
          }}
        >
          {availableEnvironments.map(env => (
            <option key={env} value={env}>
              {ENVIRONMENT_LABELS[env]} ({env})
            </option>
          ))}
        </select>
        <span style={{
          padding: '6px 12px',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 'bold',
          color: 'white',
          backgroundColor: getEnvironmentColor(selectedEnvironment)
        }}>
          {selectedEnvironment}
        </span>
      </div>

      <div className="mode-tabs">
        <button
          className={`tab-button ${mode === 'single' ? 'active' : ''}`}
          onClick={() => setMode('single')}
        >
          Single Queue
        </button>
        <button
          className={`tab-button ${mode === 'bulk' ? 'active' : ''}`}
          onClick={() => setMode('bulk')}
          disabled={isStandardUser}
          title={isStandardUser ? 'Bulk upload is not available for STANDARD users' : ''}
          style={isStandardUser ? { 
            opacity: 0.5, 
            cursor: 'not-allowed',
            backgroundColor: '#e0e0e0'
          } : {}}
        >
          Bulk Upload (CSV)
        </button>
      </div>

      {mode === 'single' && (
        <div className="form-container">
          <form onSubmit={handleSingleSubmit} className="setup-form">
            <div className="form-section">
              <h3>Instance Information</h3>
              <div className="form-group">
                <label htmlFor="instance_id">Instance *</label>
                <select
                  id="instance_id"
                  name="instance_id"
                  value={singleForm.instance_id}
                  onChange={handleSingleFormChange}
                  required
                  disabled={loadingInstances}
                >
                  <option value="">
                    {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                  </option>
                  {instances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name} ({instance.status})
                    </option>
                  ))}
                </select>
                <small>Select an Amazon Connect instance</small>
              </div>
            </div>

            <div className="form-section">
              <h3>Queue Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Queue Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={singleForm.name}
                    onChange={handleSingleFormChange}
                    placeholder="CustomerSupport"
                    required
                  />
                  <small>1-127 characters</small>
                </div>
                <div className="form-group">
                  <label htmlFor="max_contacts">Max Contacts</label>
                  <input
                    type="number"
                    id="max_contacts"
                    name="max_contacts"
                    value={singleForm.max_contacts}
                    onChange={handleSingleFormChange}
                    placeholder="10"
                    min="0"
                  />
                  <small>Maximum number of contacts (optional)</small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={singleForm.description}
                  onChange={handleSingleFormChange}
                  placeholder="Queue for customer support inquiries"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="hours_of_operation_id">Hours of Operation ID *</label>
                <input
                  type="text"
                  id="hours_of_operation_id"
                  name="hours_of_operation_id"
                  value={singleForm.hours_of_operation_id}
                  onChange={handleSingleFormChange}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  required
                />
                <small>UUID or ARN of the hours of operation</small>
              </div>
            </div>

            <div className="form-section">
              <h3>Outbound Caller Configuration (Optional)</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="outbound_caller_id_name">Caller ID Name</label>
                  <input
                    type="text"
                    id="outbound_caller_id_name"
                    name="outbound_caller_id_name"
                    value={singleForm.outbound_caller_id_name}
                    onChange={handleSingleFormChange}
                    placeholder="Support Team"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="outbound_caller_id_number_id">Caller ID Number ID</label>
                  <input
                    type="text"
                    id="outbound_caller_id_number_id"
                    name="outbound_caller_id_number_id"
                    value={singleForm.outbound_caller_id_number_id}
                    onChange={handleSingleFormChange}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  />
                  <small>Phone number ID for outbound calls</small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="outbound_flow_id">Outbound Flow ID</label>
                <input
                  type="text"
                  id="outbound_flow_id"
                  name="outbound_flow_id"
                  value={singleForm.outbound_flow_id}
                  onChange={handleSingleFormChange}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
                <small>Contact flow for outbound calls</small>
              </div>
            </div>

            <div className="form-section">
              <h3>Quick Connects (Optional)</h3>
              <div className="form-group">
                <label htmlFor="quick_connect_ids">Quick Connect IDs</label>
                <input
                  type="text"
                  id="quick_connect_ids"
                  name="quick_connect_ids"
                  value={singleForm.quick_connect_ids}
                  onChange={handleSingleFormChange}
                  placeholder="qc-111, qc-222, qc-333"
                />
                <small>Comma-separated list of quick connect IDs</small>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Creating Queue...' : 'Create Queue'}
              </button>
            </div>
          </form>
        </div>
      )}

      {mode === 'bulk' && (
        <div className="form-container">
          <div className="info-box">
            <h3>📋 CSV Format Requirements</h3>
            <p>Your CSV file should have the following columns:</p>
            <code>
              Name, Description, HoursOfOperationId, MaxContacts, OutboundCallerIdName, 
              OutboundCallerIdNumberId, OutboundFlowId, QuickConnectIds
            </code>
            <p className="mt-2">
              <strong>Note:</strong> QuickConnectIds should be semicolon-separated (e.g., qc-111;qc-222;qc-333)
            </p>
          </div>

          <form onSubmit={handleBulkSubmit} className="setup-form">
            <div className="form-section">
              <h3>Instance Information</h3>
              <div className="form-group">
                <label htmlFor="bulk_instance_id">Instance *</label>
                <select
                  id="bulk_instance_id"
                  name="instance_id"
                  value={bulkForm.instance_id}
                  onChange={handleBulkFormChange}
                  required
                  disabled={loadingInstances}
                >
                  <option value="">
                    {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                  </option>
                  {instances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name} ({instance.status})
                    </option>
                  ))}
                </select>
                <small>Select an Amazon Connect instance</small>
              </div>
            </div>

            <div className="form-section">
              <h3>S3 CSV Location</h3>
              <div className="form-group">
                <label htmlFor="s3_bucket">S3 Bucket Name *</label>
                <input
                  type="text"
                  id="s3_bucket"
                  name="s3_bucket"
                  value={bulkForm.s3_bucket}
                  onChange={handleBulkFormChange}
                  placeholder="my-connect-bucket"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="s3_key">S3 Object Key *</label>
                <input
                  type="text"
                  id="s3_key"
                  name="s3_key"
                  value={bulkForm.s3_key}
                  onChange={handleBulkFormChange}
                  placeholder="queues/sample_queues.csv"
                  required
                />
                <small>Full path to the CSV file in the S3 bucket</small>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Processing...' : 'Process Bulk Upload'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Processing your request...</p>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}

      {result && (
        <div className="alert alert-success">
          <h4>Success</h4>
          {mode === 'single' ? (
            <div>
              <p><strong>Queue created successfully!</strong></p>
              <p><strong>Queue ID:</strong> {result.result?.QueueId || 'N/A'}</p>
              <p><strong>Queue Name:</strong> {singleForm.name}</p>
              {result.result?.QueueArn && (
                <p><strong>Queue ARN:</strong> {result.result.QueueArn}</p>
              )}
            </div>
          ) : (
            <div>
              <p><strong>Bulk operation completed!</strong></p>
              <p><strong>Total processed:</strong> {result.total || 0}</p>
              <p><strong>Successful:</strong> {result.success || 0}</p>
              <p><strong>Failed:</strong> {result.failure || 0}</p>
              {result.results && result.results.length > 0 && (
                <details className="mt-2">
                  <summary>View Details</summary>
                  <pre>{JSON.stringify(result.results, null, 2)}</pre>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SetupQueuePage;
